import { createEnv } from '@t3-oss/env-nextjs';
import { vercel } from '@t3-oss/env-nextjs/presets-zod';
import { z } from 'zod';

export const env = createEnv({
  server: {
    API_URL: z.url(),

    MAINTENANCE: z.string().optional().default(''),

    CLERK_PUBLISHABLE_KEY: z.string(),
    CLERK_SECRET_KEY: z.string(),

    SENTRY_AUTH_TOKEN: z.string().optional(),

    S3_BUCKET: z.string(),
    S3_REGION: z.string(),
    AWS_ACCESS_KEY_ID: z.string(),
    AWS_SECRET_ACCESS_KEY: z.string(),

    POSTHOG_KEY: z.string(),

    /**
     * HMAC secret for signed journey-share personalisation (see
     * features/share-artifact/_utils/journey-personalization.ts). Optional on
     * purpose: absent, shares mint no personalisation and /journey renders its
     * generic form, so a missing var degrades the page rather than the build.
     */
    JOURNEY_SHARE_SIGNING_SECRET: z.string().min(32).optional(),

    FREE_EXTERNSHIPS: z.string().optional().default(''),
    EXCLUDED_EXTERNSHIPS_FROM_CONTROL_GROUP: z.string().optional().default(''),
    EXCLUDED_EXTERNSHIPS_FROM_EXPERIMENT_GROUP: z.string().optional().default(''),
    AI_FEEDBACK_BLACKLISTED_EXTERNSHIP_IDS: z.string().optional().default(''),
  },
  client: {
    NEXT_PUBLIC_API_URL: z.url(),

    NEXT_PUBLIC_APPS_API_URL: z.url().optional(),

    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string(),
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: z.string(),
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: z.string(),
    NEXT_PUBLIC_CLERK_SIGN_UP_FORCE_REDIRECT_URL: z.string().nullish(),
    NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL: z.string().nullish(),

    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string(),

    NEXT_PUBLIC_SEGMENT_WRITE_KEY: z.string().optional(),

    NEXT_PUBLIC_POSTHOG_KEY: z.string(),

    NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: z.string().optional(),

    /**
     * Google OAuth client id for the One Tap prompt on the sign-up screen. Must be the SAME client id
     * the Clerk instance holds under its Google connection's custom credentials, and that client must
     * list this app's origin under "Authorized JavaScript origins" in Google Cloud. Unset means no
     * prompt, whatever the flag says: the component renders nothing without it.
     */
    NEXT_PUBLIC_GOOGLE_ONE_TAP_CLIENT_ID: z.string().optional(),

    /** Cookiebot domain-group id. Not a secret — already visible in any page's rendered HTML. */
    NEXT_PUBLIC_COOKIEBOT_ID: z.string().optional(),

    NEXT_PUBLIC_DEFAULT_PAYMENT_PLAN_PRICE_ID: z.string().optional(),

    NEXT_PUBLIC_FREE_EXTERNSHIPS: z.string().optional().default(''),
    NEXT_PUBLIC_EXCLUDED_EXTERNSHIPS_FROM_CONTROL_GROUP: z.string().optional().default(''),
    NEXT_PUBLIC_EXCLUDED_EXTERNSHIPS_FROM_EXPERIMENT_GROUP: z.string().optional().default(''),

    /**
     * Google Apps Script web-app URL that appends Experiment 4 preview leads to a spreadsheet.
     *
     * Optional on purpose. Unset means the preview still runs and still emits its analytics events;
     * only the spreadsheet row is skipped. That keeps local dev, Storybook and preview deployments
     * from writing into the real sheet, and it makes the capture killable without a code deploy.
     *
     * This is a PUBLIC value and is spammable by anyone who reads the bundle. It writes to a
     * spreadsheet, so the blast radius is junk rows, but do not point it at anything that matters.
     */
    NEXT_PUBLIC_LABS_PREVIEW_LEAD_URL: z.url().optional(),

    NEXT_PUBLIC_PORTFOLIO_ASSETS_CDN_BASE_URL: z.url().optional().default('https://portfolio-dev.extern.com'),

    /** Origin for public portfolio links, e.g. https://www.extern.com — the /portfolios path is hardcoded in code. */
    NEXT_PUBLIC_PORTFOLIO_PUBLIC_BASE_URL: z.url().optional().default('https://www.extern.com'),
  },
  shared: {
    NODE_ENV: z.enum(['development', 'production', 'test']),
  },
  experimental__runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,

    NEXT_PUBLIC_APPS_API_URL: process.env.NEXT_PUBLIC_APPS_API_URL,

    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,

    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_UP_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_FORCE_REDIRECT_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_UP_FORCE_REDIRECT_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL,

    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,

    NEXT_PUBLIC_SEGMENT_WRITE_KEY: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,

    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,

    NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,

    NEXT_PUBLIC_GOOGLE_ONE_TAP_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_ONE_TAP_CLIENT_ID,

    NEXT_PUBLIC_COOKIEBOT_ID: process.env.NEXT_PUBLIC_COOKIEBOT_ID,

    NEXT_PUBLIC_DEFAULT_PAYMENT_PLAN_PRICE_ID: process.env.NEXT_PUBLIC_DEFAULT_PAYMENT_PLAN_PRICE_ID,

    NEXT_PUBLIC_FREE_EXTERNSHIPS: process.env.NEXT_PUBLIC_FREE_EXTERNSHIPS,
    NEXT_PUBLIC_EXCLUDED_EXTERNSHIPS_FROM_CONTROL_GROUP:
      process.env.NEXT_PUBLIC_EXCLUDED_EXTERNSHIPS_FROM_CONTROL_GROUP,
    NEXT_PUBLIC_EXCLUDED_EXTERNSHIPS_FROM_EXPERIMENT_GROUP:
      process.env.NEXT_PUBLIC_EXCLUDED_EXTERNSHIPS_FROM_EXPERIMENT_GROUP,

    NEXT_PUBLIC_LABS_PREVIEW_LEAD_URL: process.env.NEXT_PUBLIC_LABS_PREVIEW_LEAD_URL,

    NEXT_PUBLIC_PORTFOLIO_ASSETS_CDN_BASE_URL: process.env.NEXT_PUBLIC_PORTFOLIO_ASSETS_CDN_BASE_URL,

    NEXT_PUBLIC_PORTFOLIO_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_PORTFOLIO_PUBLIC_BASE_URL,
  },
  extends: [vercel()],
  skipValidation: !!process.env.CI || !!process.env.SKIP_ENV_VALIDATION || process.env.npm_lifecycle_event === 'lint',
});
